import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Check, Users, Settings, Zap, BarChart3, Target, 
  Lock, Workflow, PenTool, ArrowRight, Sparkles,
  TrendingUp, Calendar, Mail, Phone, FileText
} from 'lucide-react';

function CRMPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const customizationFeatures = [
    {
      icon: PenTool,
      title: 'Diseño a Tu Medida',
      description: 'Creamos un CRM que se adapta perfectamente a tu proceso de ventas único, no al revés.'
    },
    {
      icon: Workflow,
      title: 'Flujos Personalizados',
      description: 'Configura cada etapa de tu pipeline según tu metodología de ventas específica.'
    },
    {
      icon: Settings,
      title: 'Campos Ilimitados',
      description: 'Crea todos los campos personalizados que necesites sin restricciones ni costos adicionales.'
    },
    {
      icon: Sparkles,
      title: 'Automatizaciones Únicas',
      description: 'Diseñamos automatizaciones específicas para tu negocio, no plantillas genéricas.'
    },
    {
      icon: Users,
      title: 'Roles a Medida',
      description: 'Define permisos y roles según tu estructura organizacional real.'
    },
    {
      icon: BarChart3,
      title: 'Reportes Personalizados',
      description: 'Dashboards y métricas que muestran exactamente lo que necesitas ver.'
    }
  ];

  const industries = [
    {
      title: 'Coworks',
      description: 'Gestión completa de membresías, seguimiento de tours, automatización de renovaciones y upselling.'
    },
    {
      title: 'Inmobiliarias',
      description: 'Seguimiento de propiedades, gestión de prospectos, automatización de contratos y documentación.'
    },
    {
      title: 'Servicios B2B',
      description: 'Pipeline de proyectos, gestión de propuestas, seguimiento de negociaciones y facturación.'
    },
    {
      title: 'eCommerce',
      description: 'Seguimiento de clientes corporativos, gestión de mayoristas y automatización de recompras.'
    },
    {
      title: 'Manufactura',
      description: 'Gestión de cotizaciones complejas, seguimiento de órdenes y relación con distribuidores.'
    },
    {
      title: 'Tu Industria',
      description: 'Diseñamos el CRM perfecto para tu proceso específico, sin importar tu industria.'
    }
  ];

  const features = [
    {
      icon: Target,
      title: 'Pipeline Visual',
      description: 'Visualiza tu embudo de ventas con etapas personalizadas para tu proceso.'
    },
    {
      icon: Calendar,
      title: 'Seguimiento Inteligente',
      description: 'Recordatorios automáticos y seguimientos programados según tu estrategia.'
    },
    {
      icon: Mail,
      title: 'Email Integrado',
      description: 'Sincronización bidireccional con tu email corporativo y plantillas personalizadas.'
    },
    {
      icon: Phone,
      title: 'Registro de Llamadas',
      description: 'Documenta automáticamente todas tus interacciones con clientes.'
    },
    {
      icon: FileText,
      title: 'Documentación',
      description: 'Gestiona propuestas, contratos y documentos dentro del CRM.'
    },
    {
      icon: TrendingUp,
      title: 'Analytics en Tiempo Real',
      description: 'Métricas de ventas actualizadas al instante con tus KPIs personalizados.'
    },
    {
      icon: Zap,
      title: 'Automatizaciones',
      description: 'Automatiza tareas repetitivas según tus reglas de negocio.'
    },
    {
      icon: Lock,
      title: 'Seguridad Total',
      description: 'Tus datos protegidos con encriptación y cumplimiento normativo.'
    }
  ];

  const comparisonPoints = [
    {
      standard: 'Plantillas predefinidas',
      cloudbase: 'Diseño 100% personalizado a tu proceso'
    },
    {
      standard: 'Adapta tu proceso al software',
      cloudbase: 'El CRM se adapta a tu proceso'
    },
    {
      standard: 'Campos y etapas limitadas',
      cloudbase: 'Personalización ilimitada'
    },
    {
      standard: 'Implementación genérica',
      cloudbase: 'Onboarding dedicado a tu negocio'
    },
    {
      standard: 'Soporte por ticket',
      cloudbase: 'Consultor asignado permanente'
    },
    {
      standard: 'Costos por usuario',
      cloudbase: 'Precio fijo por empresa'
    }
  ];

  const benefits = [
    {
      stat: '10x',
      description: 'Más rápido que implementar un CRM estándar'
    },
    {
      stat: '100%',
      description: 'Adaptado a tu proceso único'
    },
    {
      stat: '0',
      description: 'Curva de aprendizaje - diseñado para ti'
    },
    {
      stat: '24/7',
      description: 'Soporte dedicado en español'
    }
  ];

  return (
    <>
      <SEOHead
        title="CRM a Medida - Cloudbase"
        description="No un CRM estándar. Diseñamos un sistema a medida que se adapta perfectamente a tu proceso de ventas único. Sin plantillas genéricas."
        keywords="CRM personalizado, CRM a medida, software CRM, gestión de ventas, pipeline personalizado"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                CRM Nexus
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Tu CRM, Diseñado a la Medida
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                No vendemos plantillas genéricas. Diseñamos un traje a medida para tu proceso de ventas único. El CRM se adapta a ti, no tú al CRM.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Consultar Mi Proyecto
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Casos de Éxito
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>100% Personalizable</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Implementación Express</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Consultor Dedicado</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Sin Costos Ocultos</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Custom CRM Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                ¿Por Qué un CRM a Medida?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Porque tu negocio es único. Tu proceso de ventas es único. 
                Tu CRM debería serlo también.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {customizationFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="p-8 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow"
                  >
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                CRM Estándar vs. Cloudbase CRM
              </h2>
              <p className="text-xl text-gray-600">
                La diferencia está en los detalles
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-2 gap-px bg-gray-200">
                <div className="bg-gray-100 p-6 text-center font-bold text-gray-900">
                  CRM Estándar
                </div>
                <div className="bg-green-50 p-6 text-center font-bold text-green-900">
                  Cloudbase CRM
                </div>
              </div>
              
              {comparisonPoints.map((point, index) => (
                <div key={index} className="grid grid-cols-2 gap-px bg-gray-200">
                  <div className="bg-white p-6 text-gray-600 flex items-center">
                    <span>{point.standard}</span>
                  </div>
                  <div className="bg-green-50 p-6 text-gray-900 font-semibold flex items-center">
                    <Check className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                    <span>{point.cloudbase}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Adaptado a Tu Industria
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Cada industria tiene sus particularidades. 
                Diseñamos tu CRM con las funcionalidades específicas que necesitas.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className="p-8 border-2 border-gray-200 rounded-lg hover:border-green-600 transition-colors"
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {industry.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{industry.description}</p>
                  <a
                    href="/demo"
                    className="text-green-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    Conocer más <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Funcionalidades Completas
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Todo lo que necesitas en un CRM profesional, 
                personalizado según tu negocio
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="p-6 bg-white rounded-lg hover:shadow-md transition-shadow"
                  >
                    <Icon className="w-10 h-10 text-green-600 mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Stats */}
        <section className="py-20 bg-black text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Resultados Medibles
              </h2>
              <p className="text-xl text-gray-300">
                Nuestros clientes experimentan mejoras significativas
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-green-500 mb-4">
                    {benefit.stat}
                  </div>
                  <p className="text-lg text-gray-300">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Cómo Funciona
              </h2>
              <p className="text-xl text-gray-600">
                De la consulta a la implementación en tiempo récord
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Consulta Inicial
                </h3>
                <p className="text-gray-600">
                  Analizamos tu proceso de ventas actual y necesidades específicas
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Diseño Personalizado
                </h3>
                <p className="text-gray-600">
                  Creamos el blueprint de tu CRM adaptado a tu negocio
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Implementación
                </h3>
                <p className="text-gray-600">
                  Configuramos y migramos tus datos con mínima interrupción
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Capacitación
                </h3>
                <p className="text-gray-600">
                  Entrenamos a tu equipo y te acompañamos en cada paso
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para Transformar tu Gestión de Ventas?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Agenda una consulta gratuita y descubre cómo podemos diseñar 
              el CRM perfecto para tu negocio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-green-600 hover:bg-gray-100 px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
                onClick={() => window.location.href = '/demo'}
              >
                Agendar Consulta Gratuita
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                onClick={() => window.location.href = '/demo'}
              >
                Ver Casos de Éxito
              </Button>
            </div>
            <p className="mt-8 text-white/70 text-sm">
              Sin compromiso • Consulta personalizada • Propuesta en 48 horas
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default CRMPage;